import { useEffect, useRef, useState } from 'react';
import { IoEllipsisHorizontal } from 'react-icons/io5';

/**
 * The less common row actions, behind one button, so a row shows its main
 * action plus "More" instead of five buttons (which became a five-button
 * footer on every mobile card).
 *
 * items: [{ label, icon, onClick, danger?, disabled?, title?, hidden? }]
 */
const RowMenu = ({ items, label = 'More', ariaLabel }) => {
  // Fixed-position coordinates: the table sits in a horizontal scroll box,
  // which would clip an absolutely positioned popover on the last rows.
  const [position, setPosition] = useState(null);
  const open = position !== null;
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const close = () => setPosition(null);

  const toggle = () => {
    if (open) {
      close();
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setPosition({
      right: Math.max(8, window.innerWidth - rect.right),
      ...(spaceBelow < 220 ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 })
    });
  };
  const visible = items.filter((item) => !item.hidden);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) close();
    };
    const onKey = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    // A fixed popover would drift away from its row when anything scrolls
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (visible.length === 0) return null;

  return (
    <span className="admin-row-menu" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={toggle}
      >
        <IoEllipsisHorizontal aria-hidden="true" /> {label}
      </button>
      {open && (
        <span className="admin-row-menu-list" role="menu" style={position}>
          {visible.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={item.danger ? 'is-danger' : ''}
              disabled={item.disabled}
              title={item.title}
              onClick={() => {
                close();
                item.onClick();
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
};

export default RowMenu;
